import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ordersApi, type Order, type OrderProforma as Proforma } from "../../api/orders";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";
import { formatDate, formatMoneyCode } from "../../lib/format";
import { card, cardHead, cardTitle, hint } from "./orderCardStyles";

/**
 * The ERP pro forma invoice the approved order became, as the ERP holds it now: number, day and the day it holds
 * until, the total and the payment terms, and with payment in advance where to pay and the reference to pay by; the
 * document itself as a PDF. For whoever sees the order, the customer first, who no longer asks for it by mail.
 */
export function OrderProforma({ order }: { order: Order }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const jezik = i18n.resolvedLanguage;
  const [proforma, setProforma] = useState<Proforma | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setProforma(null);
    setFailure(null);
    ordersApi.proforma(order.id)
      .then((p) => { if (live) setProforma(p); })
      .catch((e: ApiError) => { if (live) setFailure(e.code ?? "FAILED"); });
    return () => { live = false; };
  }, [order.id, order.erpProformaNumber]);

  async function download(number: string) {
    setBusy(true);
    try {
      const res = await fetch(`/api/exports/orders/${order.id}/proforma.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error(t("orderProforma.downloadFailed", "The PDF could not be made; try again."));
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      // The ERP's number has a slash, which a file name cannot carry.
      a.download = `predracun-${number.replace(/[^0-9A-Za-z-]/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const reason = failure === "ERP_UNAVAILABLE"
    ? t("orderProforma.unavailable", "The ERP is not answering right now; the pro forma invoice will be here when it does.")
    : failure === "PROFORMA_GONE"
      ? t("orderProforma.gone", "The ERP no longer has this pro forma invoice.")
      : t("orderProforma.failed", "The pro forma invoice cannot be read.");

  return (
    <section style={card} data-testid="order-proforma">
      <header style={cardHead}>
        <h2 style={cardTitle}>{t("orderProforma.title", "Pro forma invoice")}</h2>
        {proforma && (
          <button type="button" className="btn sm" onClick={() => void download(proforma.number)} disabled={busy}>
            {t("orderProforma.download", "Download PDF")}
          </button>
        )}
      </header>
      {proforma ? (
        // A label above its value, as on the ERP terms: a document number beside a long label breaks.
        <dl style={facts}>
          <Fact label={t("orderProforma.number", "Number")}>{proforma.number}</Fact>
          <Fact label={t("orderProforma.issuedOn", "Date")}>{formatDate(proforma.issuedOn, jezik)}</Fact>
          <Fact label={t("orderProforma.validUntil", "Valid until")}>{formatDate(proforma.validUntil, jezik)}</Fact>
          <Fact label={proforma.domestic
            ? t("orderProforma.totalWithVat", "Total with VAT")
            : t("orderProforma.totalExport", "Total (export, without VAT)")}>
            <strong>{formatMoneyCode(proforma.total, proforma.currency, jezik, naPare)}</strong>
          </Fact>
          <Fact label={t("orderProforma.paymentTerms", "Payment terms")}>{proforma.paymentTerms || "—"}</Fact>
          {/* Paid in advance, the customer pays by this document: where to, and by what reference. */}
          {proforma.inAdvance && proforma.payTo.length > 0 && (
            <>
              <Fact label={t("orderProforma.payTo", "Pay into")}>
                {proforma.payTo.map((a) => <span key={a} style={{ display: "block" }}>{a}</span>)}
              </Fact>
              <Fact label={t("orderProforma.reference", "Payment reference")}>{proforma.paymentReference}</Fact>
            </>
          )}
        </dl>
      ) : (
        <p style={hint}>
          {failure === null
            ? t("orderProforma.loading", "Reading the pro forma invoice {{number}} from the ERP…", {
              number: order.erpProformaNumber ?? "",
            })
            : reason}
        </p>
      )}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt style={factLabel}>{label}</dt>
      <dd style={factValue}>{children}</dd>
    </div>
  );
}

// An amount to the para: the dinar's default drops the decimals.
const naPare: Intl.NumberFormatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
const facts: CSSProperties = { display: "flex", flexDirection: "column", gap: 10, margin: 0 };
const factLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const factValue: CSSProperties = { margin: 0, fontSize: 13, color: "var(--ink)" };
